import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useDispatch, useSelector } from 'react-redux';
import * as PropTypes from 'prop-types';
import {
  changeRowsNumber,
  searchForLocations
} from '../../../redux/slices/location/locationsList';
import {
  changeRowsNumber as cloudsChangeRowsNumber,
  searchForClouds
} from '../../../redux/slices/cloud/cloudsList';
import { Stack } from 'react-bootstrap';
import Symbol from '../../utils/symbol';
import SearchField from '../../utils/datatable/searchField';
import IsLoadingIndicator from '../../isLoadingIndicator';
import { CLOUD, COCKPIT } from '../../../config/constants';
import { useTranslate } from '@tolgee/react';

function SelectLocationsTable({
  type,
  setSelectedRows,
  selectedRows,
  allRowsSelected = false
}) {
  const [action] = useState({ fromUser: false }); // this is a way to have an instant-changing state
  const dispatch = useDispatch();
  const { t } = useTranslate();
  const columns = [
    {
      name: t('accounts.invites.table.name'),
      show: true,
      selector: (row) => (
        <Stack
          direction={'horizontal'}
          gap={3}
          style={{ width: 300, padding: 4 }}
          className="user-select-none cursor-pointer"
          onClick={() => handleRowClicked(row)}
        >
          {row.image ? (
            <Symbol img={row.image} />
          ) : (
            <Symbol initials={`${row.initials}`} />
          )}

          <div className="d-flex justify-content-start flex-column">
            <span className="text-gray-400 fw-semi-bold d-block fs-7">
              {row.name}
            </span>
          </div>
        </Stack>
      )
    },
    {
      name: t('accounts.invites.table.created_by'),
      show: true,
      selector: (row) => (
        <div
          className="user-select-none cursor-pointer"
          onClick={() => handleRowClicked(row)}
        >
          {row.created_by.name}
        </div>
      )
    }
  ].filter((item) => item.show);

  // number of rows per page

  const handleRowsPerPageChange = (rowsNb) => {
    dispatch(changeRowsNumber(rowsNb));
    dispatch(cloudsChangeRowsNumber(rowsNb));
  };
  // redux states
  const { isLoading, count, rowsNumber, locations } = useSelector(
    (state) => state.locationsList
  );
  const {
    isLoading: cloudsIsLoading,
    count: cloudsCount,

    clouds,
    rowsNumber: cloudsRowsNumber
  } = useSelector((state) => state.cloudsList);
  // initial load

  // get selected page data
  const [search, setSearch] = useState(null);

  // for search
  const prevSearchRef = useRef(null);

  useEffect(() => {
    // At this point, prevSearchRef.current will contain the previous search value
    if (prevSearchRef.current === search) return;

    const getData = setTimeout(() => {
      if (type === CLOUD) dispatch(searchForClouds({ search }));
      else dispatch(searchForLocations({ search }));
    }, 500);

    // Update the ref with the current search value current everything else
    prevSearchRef.current = search;

    return () => clearTimeout(getData);
  }, [search]);

  const subHeaderComponentMemo = useMemo(() => {
    const handleSearch = (e) => {
      setSearch(e.target.value);
    };

    const profileGroupTranslation = t('profile_groups');
    const cloudTranslation = t('clouds');

    return (
      <div className="d-flex justify-content-between w-100 py-5  align-items-center">
        <div>
          <h2 style={{ margin: 0, paddingLeft: 0 }}>
            {type === COCKPIT
              ? `${profileGroupTranslation} (${
                  allRowsSelected ? locations.length : selectedRows.length
                }/${locations.length})`
              : `${cloudTranslation} (${
                  allRowsSelected ? clouds.length : selectedRows.length
                }/${clouds.length})`}
          </h2>
          <p className="text-muted">{t(`accounts.invites.${type}_subtitle`)}</p>
        </div>
        <div className="d-flex flex-row align-items-center">
          <div className="d-flex justify-content-end">
            {/*<SearchField onChange={handleSearch} placeholder={t('search')} />*/}
          </div>
        </div>
      </div>
    );
  }, [search, type, selectedRows, locations, allRowsSelected, clouds]);
  const customStyles = {
    headCells: {
      style: {
        paddingLeft: '8px', // override the cell padding for head cells
        paddingRight: '8px',
        color: 'gray'
      }
    },
    table: {
      style: {
        color: 'red',
        backgroundColor: 'gray',
        boxShadow: 'none'
      }
    },
    rows: {
      style: {
        cursor: 'pointer',
        paddingTop: 10,
        paddingBottom: 10,
        '&:not(:last-of-type)': {
          borderBottomStyle: 'dashed',
          '&:not(.rdt-selected)': {
            backgroundColor: '#FFFFFF'
          }
        },
        highlightOnHoverStyle: {
          backgroundColor: '#E5E5E5'
        }
      }
    },
    headRow: {
      style: {
        paddingTop: 10,
        paddingBottom: 10,

        borderBottomStyle: 'dashed'
      }
    },
    pagination: {
      style: {
        borderTopStyle: 'dashed'
      }
    }
  };

  const handleRowSelectedChange = ({ selectedRows: rows }) => {
    if (!action.fromUser) return;
    if (rows?.length === selectedRows.length) return;
    setSelectedRows(rows.map((r) => r.uuid));
  };
  const rowStyle = () => {
    return [
      {
        when: (row) => selectedRows.some((item) => item === row.uuid),
        classNames: ['bg-light-primary']
      }
    ];
  };

  const handleMouseEnter = () => {
    action.fromUser = true;
  };
  const handleRowClicked = (row) => {
    if (selectedRows?.includes(row.uuid)) {
      setSelectedRows(selectedRows.filter((item) => item !== row.uuid));
    } else {
      setSelectedRows([...selectedRows, row.uuid]);
    }
  };

  return (
    <DataTable
      selectableRows
      onSelectedRowsChange={(e) =>
        !allRowsSelected && handleRowSelectedChange(e)
      }
      onRowClicked={handleRowClicked}
      columns={columns}
      data={type === CLOUD ? clouds : locations}
      subHeader
      subHeaderComponent={subHeaderComponentMemo}
      pagination
      paginationTotalRows={type === CLOUD ? cloudsCount : count}
      paginationComponentOptions={{
        rangeSeparatorText: t('accounts.invites.table.of'),
        rowsPerPageText: t('accounts.invites.table.entries_per_page')
      }}
      progressPending={isLoading || cloudsIsLoading}
      paginationPerPage={type === CLOUD ? cloudsRowsNumber : rowsNumber}
      onChangeRowsPerPage={handleRowsPerPageChange}
      progressComponent={
        <div style={{ height: 200 }}>
          <IsLoadingIndicator />
        </div>
      }
      paginationRowsPerPageOptions={[1, 5, 10, 15, 20]}
      customStyles={customStyles}
      conditionalRowStyles={selectedRows && rowStyle()}
      noTableCard
      selectableRowSelected={(row) =>
        selectedRows?.includes(row.uuid) || allRowsSelected
      }
      onRowMouseEnter={handleMouseEnter}
      selectableRowsComponent={SelectableRowsComponentWrapper}
      selectableRowsComponentProps={{ allRowsSelected }}
    />
  );
}

SelectLocationsTable.propTypes = {
  type: PropTypes.string,
  setSelectedRows: PropTypes.func,
  selectedRows: PropTypes.any,
  allRowsSelected: PropTypes.bool
};

const SelectableRowsComponent = forwardRef((defaultProps, ref) => {
  return (
    <input
      ref={ref}
      className="form-check-input form-check-input-sm w-15px h-15px"
      type="checkbox"
      style={{ border: '2px solid #bcbcbc !important' }}
      value=""
      id="flexCheckDefault"
      {...defaultProps}
      disabled={defaultProps.allRowsSelected}
    />
  );
});

SelectableRowsComponent.displayName = 'SelectableRowsComponent';
const SelectableRowsComponentWrapper = (props) => {
  const { allRowsSelected, ...rest } = props;
  return (
    <SelectableRowsComponent {...rest} allRowsSelected={allRowsSelected} />
  );
};
SelectableRowsComponentWrapper.propTypes = {
  allRowsSelected: PropTypes.bool
};
export default SelectLocationsTable;
